import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AccountDetailPage } from '../../features/accounts/pages/AccountDetailPage.js';
import { AccountsPage } from '../../features/accounts/pages/AccountsPage.js';
import { LoginPage } from '../../features/auth/pages/LoginPage.js';
import { CategoriesPage } from '../../features/categories/pages/CategoriesPage.js';
import { FamilyPage } from '../../features/family/pages/FamilyPage.js';
import { MonthlyClosePage } from '../../features/monthly-close/pages/MonthlyClosePage.js';
import { PendingPage } from '../../features/pending/pages/PendingPage.js';
import { ReconciliationPage } from '../../features/reconciliation/pages/ReconciliationPage.js';
import { ReportPage } from '../../features/report/pages/ReportPage.js';
import { SetupPage } from '../../features/setup/pages/SetupPage.js';
import { RecurringDetailPage } from '../../features/recurring/pages/RecurringDetailPage.js';
import { RecurringPage } from '../../features/recurring/pages/RecurringPage.js';
import { HomePage } from '../../features/home/pages/HomePage.js';
import { IncomeSourcesPage } from '../../features/income/pages/IncomeSourcesPage.js';
import { InvestmentPage } from '../../features/investment/pages/InvestmentPage.js';
import { QuickAddPage } from '../../features/transactions/pages/QuickAddPage.js';
import { TimelinePage } from '../../features/transactions/pages/TimelinePage.js';
import { InstallGuidePage } from '../../features/install/pages/InstallGuidePage.js';
import { ProfilePage } from '../../features/profile/pages/ProfilePage.js';
import { AppShell } from '../shell/AppShell.js';
import { useSession } from '../providers/sessionContext.js';
import { RequireAuth } from './RequireAuth.js';

/** Routes are product screens with Vietnamese paths, not implementation details (skill 17). */
export function AppRouter() {
  const session = useSession();

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/dang-nhap"
          element={session.status === 'signedIn' ? <Navigate to="/" replace /> : <LoginPage />}
        />
        {/* Reachable while signed out: it is the page a new iPhone user is sent to. */}
        <Route path="/cai-dat-ung-dung" element={<InstallGuidePage />} />

        {/* Quick Add is a full-screen sheet on purpose (spec 22.1): no bottom navigation to
            mis-tap with the keyboard up, and ✕ goes back where it was opened from. */}
        <Route
          path="/them"
          element={
            <RequireAuth>
              <QuickAddPage />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="/ho-so" element={<ProfilePage />} />
          {/* Tài sản: tài sản / tiêu sản (bước 1.1); dòng tiền theo sau ở bước 1.4. */}
          <Route path="/tai-san" element={<AccountsPage />} />
          <Route path="/tai-san/:accountId" element={<AccountDetailPage />} />

          {/* Nguồn thu và danh mục là màn thiết lập — vào từ menu, không chiếm ô nav nào. */}
          <Route path="/nguon-thu" element={<IncomeSourcesPage />} />
          <Route path="/danh-muc" element={<CategoriesPage />} />
          <Route path="/dinh-ky" element={<RecurringPage />} />
          <Route path="/dinh-ky/:templateId" element={<RecurringDetailPage />} />
          <Route path="/doi-soat" element={<ReconciliationPage />} />
          {/* Chờ duyệt: khoản chuyển khoản lấy từ email ngân hàng, chưa vào sổ (AL1). Vào từ
              thẻ trên Trang chủ — chỉ hiện khi có khoản chờ. */}
          <Route path="/cho-duyet" element={<PendingPage />} />
          <Route path="/viec-thang" element={<MonthlyClosePage />} />
          {/* Thiết lập ban đầu: checklist go-live (bước 1.9). Vào từ menu — dùng vài lần
              đầu rồi thôi, nên không chiếm ô nav nào. */}
          <Route path="/thiet-lap" element={<SetupPage />} />
          {/* Gia đình: tạo gia đình + thêm thành viên. Tab luôn hiển thị vì đó là đường duy
              nhất để tạo gia đình lần đầu (quyết định L5). */}
          <Route path="/gia-dinh" element={<FamilyPage />} />
          {/* Không còn là tab — ⊕ và Tài sản đã gánh hai việc này — nhưng vẫn là màn hình
              trong kế hoạch, vào từ Trang chủ và từ màn Thêm giao dịch. */}
          <Route path="/giao-dich" element={<TimelinePage />} />
          {/* Trang chủ gánh dashboard của bước 1.4; màn này là phần chi tiết: Money In tách
              ổn định / không đều (B10), chi theo tính chất, và chi bất thường (§10). */}
          <Route path="/bao-cao" element={<ReportPage />} />
          {/* Đầu tư: XEM kế hoạch và tiến độ. Kế hoạch được lập bằng cách trao đổi với agent,
              không tạo trong app (quyết định X1) — màn này chỉ đọc. Tỷ trọng thực tế từng mã
              phải chờ dữ liệu nắm giữ ở Phase 3. */}
          <Route path="/dau-tu" element={<InvestmentPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
